import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const AdminMfaVerify = () => {
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { code: "" },
    mode: "onSubmit",
  });

  // Focus on code input after mount
  useEffect(() => {
    const t = setTimeout(() => setFocus("code"), 0);
    return () => clearTimeout(t);
  }, [setFocus]);

  const onSubmit = async (vals) => {
    setSubmitting(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/auth/mfa/verify`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(vals),
        },
      );

      if (!r.ok) {
        const payload = await r.json().catch(() => ({}));
        throw new Error(payload?.detail || "MFA verification failed");
      }

      reset();
      nav("/admin/leads", { replace: true });
    } catch (e) {
      alert(e.message ?? "MFA verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-3xl font-bold text-orange-500 text-center">
          BuildBright
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Two-Factor Authentication
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>

      <div
        className="mt-8 sm:mx-auto sm:w-full sm:max-w-md"
        data-testid="admin-mfa-ready"
      >
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div>
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="mfa-code"
              >
                Authentication Code
              </label>
              <input
                id="mfa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                pattern="[0-9]{6}"
                aria-invalid={!!errors.code}
                className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm text-center text-lg font-mono"
                placeholder="000000"
                {...register("code", {
                  required: "Authentication code is required",
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: "Please enter a 6-digit code",
                  },
                })}
                data-testid="mfa-code"
              />
              {errors.code && (
                <div role="alert" className="text-red-600 text-sm mt-1">
                  {errors.code.message}
                </div>
              )}
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="mfa-submit"
              >
                {submitting ? "Verifying…" : "Verify Code"}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Having trouble? Contact your system administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMfaVerify;
